/*
 * Copyright 2026 Sony Corporation
 */

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { UNCOVERED, cssOf, detailColour, overlapColour, type CoverageMode } from '../../coverage/colours';
import type { CoverageStats } from '../../coverage/coverage';
import type { SubjectPlacement } from '../../coverage/subject';
import type { CoverageSettings } from '../../hooks/use-coverage';
import { NumberField } from '../panels/NumberField';

export interface CoveragePanelProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  settings: CoverageSettings;
  onSettingsChange: (settings: CoverageSettings) => void;
  placement: SubjectPlacement;
  onPlacementChange: (placement: SubjectPlacement) => void;
  stats: CoverageStats | undefined;
}

const percent = (share: number): string => `${(share * 100).toFixed(share > 0.995 && share < 1 ? 1 : 0)}%`;

const Row = ({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) => (
  <Box className="flex items-baseline justify-between gap-2">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" className="font-mono" color={emphasis ? 'warning.main' : undefined}>
      {value}
    </Typography>
  </Box>
);

const Swatch = ({ colour, label }: { colour: string; label: string }) => (
  <Box className="flex items-center gap-1">
    <Box className="h-3 w-3 rounded-sm" sx={{ bgcolor: colour }} />
    <Typography variant="caption">{label}</Typography>
  </Box>
);

const Legend = ({ mode, settings }: { mode: CoverageMode; settings: CoverageSettings }) => {
  const target = settings.targetMmPerPx;
  if (mode === 'overlap') {
    return (
      <Box className="flex flex-wrap gap-x-3 gap-y-1">
        <Swatch colour={cssOf(UNCOVERED)} label="none" />
        {settings.minOverlap > 1 && (
          <Swatch colour={cssOf(overlapColour(1, settings.minOverlap))} label={`< ${settings.minOverlap}`} />
        )}
        <Swatch
          colour={cssOf(overlapColour(settings.minOverlap, settings.minOverlap))}
          label={`≥ ${settings.minOverlap}`}
        />
      </Box>
    );
  }
  const gradient = [0.5, 0.7, 1, 1.4, 2].map((ratio) => cssOf(detailColour(ratio * target, target))).join(', ');
  return (
    <Box>
      <Box className="h-2 rounded-sm" sx={{ background: `linear-gradient(to right, ${gradient})` }} />
      <Box className="flex justify-between">
        <Typography variant="caption">≤ {(target / 2).toFixed(2)}</Typography>
        <Typography variant="caption">{target.toFixed(2)} mm/px</Typography>
        <Typography variant="caption">≥ {(target * 2).toFixed(2)}</Typography>
      </Box>
      <Swatch colour={cssOf(UNCOVERED)} label="not seen" />
    </Box>
  );
};

/**
 * The heatmap's controls and its answer, floating over the viewport.
 *
 * The answer comes first in the reading order that matters: how much of the
 * subject is seen, how much of it is seen well enough, and where the gaps are.
 */
export const CoveragePanel = ({
  enabled,
  onEnabledChange,
  settings,
  onSettingsChange,
  placement,
  onPlacementChange,
  stats,
}: CoveragePanelProps) => {
  if (!enabled) {
    return (
      <Paper elevation={3} className="absolute left-3 top-14 z-10">
        <Tooltip title="Paint how well the devices see a person standing in the scene">
          <Button size="small" onClick={() => onEnabledChange(true)}>
            Coverage
          </Button>
        </Tooltip>
      </Paper>
    );
  }

  const set = (change: Partial<CoverageSettings>) => onSettingsChange({ ...settings, ...change });
  const move = (axis: 0 | 1 | 2, value: number) => {
    const position = [...placement.position] as [number, number, number];
    position[axis] = value;
    onPlacementChange({ ...placement, position });
  };
  const gaps = stats?.parts.filter((part) => part.uncovered > 0.01).slice(0, 3) ?? [];

  return (
    <Paper
      elevation={3}
      className="absolute left-3 top-14 z-10 flex max-h-[calc(100%-4.5rem)] w-72 flex-col gap-3 overflow-y-auto p-3"
    >
      <Box className="flex items-center justify-between">
        <Typography variant="subtitle2">Coverage</Typography>
        <IconButton size="small" aria-label="Hide coverage" onClick={() => onEnabledChange(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <ToggleButtonGroup
        size="small"
        exclusive
        fullWidth
        value={settings.mode}
        onChange={(_event, mode: CoverageMode | null) => mode && set({ mode })}
      >
        <ToggleButton value="detail">Detail</ToggleButton>
        <ToggleButton value="overlap">Overlap</ToggleButton>
      </ToggleButtonGroup>

      <Legend mode={settings.mode} settings={settings} />

      {stats && (
        <Box className="flex flex-col gap-1">
          <Row label="Seen by a device" value={percent(stats.covered)} emphasis={stats.covered < 0.99} />
          <Row label={`At ${settings.targetMmPerPx} mm/px or finer`} value={percent(stats.meetsTarget)} />
          <Row label={`By ${settings.minOverlap}+ devices`} value={percent(stats.meetsOverlap)} />
          {stats.median !== undefined && <Row label="Median detail" value={`${stats.median.toFixed(2)} mm/px`} />}
          {stats.worst && (
            <Row label={`Coarsest (${stats.worst.part})`} value={`${stats.worst.mmPerPx.toFixed(2)} mm/px`} />
          )}
          {gaps.length > 0 && (
            <Typography variant="caption" color="warning.main">
              Least seen: {gaps.map((part) => `${part.name} ${percent(part.uncovered)} unseen`).join(', ')}
            </Typography>
          )}
          {stats.cameras === 0 && (
            <Typography variant="caption" color="text.secondary">
              No devices are placed yet.
            </Typography>
          )}
        </Box>
      )}

      <Box className="grid grid-cols-2 gap-2">
        <NumberField
          label="Target"
          unit="mm/px"
          min={0.01}
          max={100}
          value={settings.targetMmPerPx}
          onCommit={(targetMmPerPx) => set({ targetMmPerPx })}
        />
        <NumberField
          label="Devices each"
          unit="×"
          digits={0}
          min={1}
          max={16}
          value={settings.minOverlap}
          onCommit={(minOverlap) => set({ minOverlap: Math.round(minOverlap) })}
        />
      </Box>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={settings.requireFocus}
            onChange={(event) => set({ requireFocus: event.target.checked })}
          />
        }
        label={<Typography variant="body2">Only count what is in focus</Typography>}
      />

      <Typography variant="caption" color="text.secondary">
        Subject
      </Typography>
      <Box className="grid grid-cols-3 gap-2">
        {(['X', 'Y', 'Z'] as const).map((label, axis) => (
          <NumberField
            key={label}
            label={label}
            unit="m"
            value={placement.position[axis]}
            onCommit={(value) => move(axis as 0 | 1 | 2, value)}
          />
        ))}
      </Box>
      <Box className="grid grid-cols-2 gap-2">
        <NumberField
          label="Height"
          unit="m"
          min={0.3}
          max={2.5}
          value={placement.height}
          onCommit={(height) => onPlacementChange({ ...placement, height })}
        />
        <NumberField
          label="Facing"
          unit="°"
          digits={0}
          value={(placement.heading * 180) / Math.PI}
          onCommit={(degrees) => onPlacementChange({ ...placement, heading: (degrees * Math.PI) / 180 })}
        />
      </Box>
    </Paper>
  );
};
